/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, forwardRef } from 'react';
import {
  findNodePath,
  setNodes,
  createElementAs,
  createComponentAs,
  useElement,
  useCaptionString,
  getPluginOptions,
  ELEMENT_IMAGE,
} from '@udecode/plate';
import { HistoryEditor } from 'slate-history';
import { offset } from '@udecode/plate-floating';
import { useReadOnly } from 'slate-react';

import Progress from 'components/Progress';
import { useUpload } from '../upload/useUpload';
import { CaptionRoot, CaptionTextarea } from '../caption/Caption';
import { PlateFloatingImage } from '../PlateFloatingImage';
import { ElementPopover } from '../../../components/popup/Floating/ElementPopover';
import { Media } from '../Media';
import { KEY_DOCUMENT_SETTINGS } from '../../document-settings/createDocumentSettingsPlugin';

import './image-element.scss';

export const ImageElement = (props) => {
  const {
    children,
    nodeProps,
    caption = {},
    popoverProps = {},
    resizableProps,
    ignoreReadOnly = false,
    element,
    editor,
  } = props;

  const { as, ...rootProps } = props;
  const { align = 'left', url: elementUrl } = element;

  const { innerWidth } = getPluginOptions(editor, KEY_DOCUMENT_SETTINGS);

  // Gets the image status from store, if it is uploading.
  const upload = useUpload(element.filename);
  const { percent, status, url: uploadUrl } = upload;

  const readOnly = useReadOnly();

  useEffect(() => {
    const applyUrlToImage = (path) => {
      setNodes(
        editor,
        { url: uploadUrl },
        {
          at: path,
        },
      );
    };
    // When image finishes uploading, url should be populated.
    // Set the url on the image node.
    if (status === 'success' && elementUrl !== uploadUrl) {
      const path = findNodePath(editor, element);
      // Need to ignore writing this operation to history because undo breaks if
      // this is recorded in the history.
      if (HistoryEditor.isHistoryEditor(editor)) {
        HistoryEditor.withoutSaving(editor, () => applyUrlToImage(path));
      }
      else {
        applyUrlToImage(path);
      }
    }
  }, [editor, element, status, elementUrl, uploadUrl]);

  return (
    <Media.Root {...rootProps}>
      <figure
        className="slate-ui-media"
        contentEditable={false}
      >
        <Media.Resizable
          className="slate-ui-media__resize"
          renderHandleLeft={(htmlProps) => (
            <div
              {...htmlProps}
              className="slate-ui-media__resize-handle slate-ui-media__resize-handle--left"
            />
          )}
          renderHandleRight={(htmlProps) => (
            <div
              {...htmlProps}
              className="slate-ui-media__resize-handle slate-ui-media__resize-handle--right"
            />
          )}
          align={align}
          readOnly={!ignoreReadOnly && readOnly}
          {...resizableProps}
        >
          <ElementPopover
            content={<PlateFloatingImage pluginKey={ELEMENT_IMAGE} />}
            floatingOptions={{
              middleware: [
                offset(({ rects }) => {
                  const gutter = 5;
                  return {
                    mainAxis: -(rects.floating.width + gutter),
                    crossAxis: -(gutter),
                  };
                }),
              ],
              placement: 'top-end',
            }}
            {...popoverProps}
          >
            {!elementUrl && percent && percent < 1 ? (
              <UploadingImage
                element={element}
                progress={percent}
                documentWidth={innerWidth}
              />
            ) : (
              <Image
                className="slate-image"
                {...nodeProps}
              />
            )}
          </ElementPopover>
        </Media.Resizable>

        {!caption.disabled && (
          <CaptionRoot
            className="slate-ui-media__caption"
          >
            <CaptionTextarea
              placeholder={caption.placeholder ?? 'Write a caption...'}
              readOnly={(!ignoreReadOnly && readOnly) || !!caption.readOnly}
            />
          </CaptionRoot>
        )}
      </figure>

      {children}
    </Media.Root>
  );
};

export const useImage = (props) => {
  const { url } = useElement();

  const captionString = useCaptionString();

  return {
    src: url,
    alt: captionString,
    draggable: true,
    ...props,
  };
};

export const Image = createComponentAs((props) => {
  const htmlProps = useImage(props);

  return createElementAs('img', htmlProps);
});

// eslint-disable-next-line
const UploadingImage = forwardRef(({ element, progress, documentWidth }, ref) => {
  const { width, height } = element;
  const heightRatio = height / width;
  const maxHeight = documentWidth * heightRatio;
  const percentage = progress * 100;

  return (
    <div
      className="slate-image slate-image--uploading"
      style={{
        height,
        width,
        maxHeight,
        maxWidth: '100%',
      }}
    >
      <Progress
        value={percentage}
        className="slate-image__progress"
      />
    </div>
  );
});
